import { useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';

import { useTranslate } from '@growtherapy/i18n';

import { useActiveBrand } from '../../../components/branding/useActiveBrand';
import { logger } from '../../../datadog/logger';
import { useAdmissionFlowCallback } from '../../../hooks/useAdmissionFlow';
import { useConnectRoomCallback } from '../../../session/useConnectRoomCallback';
import { toast } from '../../../toasts';
import { DebugMutex } from '../../../video-sdk/core/mutex';
import { validateRoomHasParticipants } from '../../../video-sdk/core/room/validators';
import { RoomValidationError } from '../../../video-sdk/core/room/validators/RoomValidationError';
import { GenericRoomError } from '../../../video-sdk/core/utils';
import { NEW_MESSAGE_TOAST_TAG } from '../../chat/useShowNewMessageToastCallback';
import { setDefaultMeetingRoomFaviconAndTitle } from '../../meeting-room/metadata';
import {
  VisitorState,
  activeConversationSidAtom,
  preSessionVibesOpenAtom,
  sessionConversationSidAtom,
  visitorNameAtom,
  visitorStateAtom,
} from '../../state';
import { usePreSessionPromptState } from '../pre-session-prompt/usePreSessionPromptState';
import { useUpdateVisitorPresenceCallback } from '../useUpdateVisitorPresenceCallback';
import { joinAttemptsAtom } from './state';
import { useClearAdmissionStateCallback } from './useClearAdmissionState';
import { useGracefullyCoerceVisitorVideoPlatformCallback } from './useCoerceVisitorVideoPlatformCallback';
import { useTrackJoinSessionError } from './useTrackJoinSessionError';

const showErrorToast = (message: string) => {
  toast.error(message, { purpose: 'system' });
};

const connectRoomMutex = new DebugMutex('connectRoomMutex');

/**
 * Visitor-specific room connection with error handling and cleanup
 */
export function useVisitorConnectRoomCallback() {
  const activeBrand = useActiveBrand();
  const t = useTranslate();
  const [connectRoom] = useConnectRoomCallback();
  const setVisitorState = useSetAtom(visitorStateAtom);
  const getVisitorName = useAtomCallback(useCallback((get) => get(visitorNameAtom), []));
  const setActiveConversationSid = useSetAtom(activeConversationSidAtom);
  const setPresessionVibesOpen = useSetAtom(preSessionVibesOpenAtom);
  const setJoinAttempts = useSetAtom(joinAttemptsAtom);
  const { clearPreSessionPrompt } = usePreSessionPromptState();
  const getSessionConversationSid = useAtomCallback(useCallback((get) => get(sessionConversationSidAtom), []));
  const clearAdmissionState = useClearAdmissionStateCallback();
  const getAdmissionFlow = useAdmissionFlowCallback();
  const gracefullyCoerceVideoPlatform = useGracefullyCoerceVisitorVideoPlatformCallback();
  const updateVisitorPresence = useUpdateVisitorPresenceCallback();
  const trackJoinSessionError = useTrackJoinSessionError();

  const visitorConnectRoom = useCallback(async () => {
    if (connectRoomMutex.isLocked()) {
      logger.warn('Failed to join room: connection already in progress');
      return null;
    }

    return connectRoomMutex.runExclusive(async () => {
      setVisitorState(VisitorState.JOINING);
      void updateVisitorPresence();
      const conversationSid = getSessionConversationSid();
      setJoinAttempts((count) => count + 1);

      const coercionResult = await gracefullyCoerceVideoPlatform();
      const [isCoercionSuccess] = coercionResult;

      if (!isCoercionSuccess) {
        const [, coercionError] = coercionResult;
        showErrorToast(t('telehealth-core:waitingRoom.thereWasAnIssueJoiningTheMeeting'));
        void trackJoinSessionError(coercionError);
        clearAdmissionState();
        setVisitorState(VisitorState.WAITING);
        void updateVisitorPresence();
        return null;
      }

      const room = await connectRoom(getVisitorName(), {
        roomValidators: [validateRoomHasParticipants],
        onError: (error) => {
          if (error instanceof RoomValidationError && error.kind === 'empty-room') {
            showErrorToast(t('telehealth-core:waitingRoom.yourProviderMayHaveLeftTheRoom'));
            logger.warn('Room has less than 1 remote participant');
            void trackJoinSessionError(error);
            clearAdmissionState();
            return;
          }

          if (
            getAdmissionFlow() === 'manual' &&
            error instanceof GenericRoomError &&
            error.kind === 'connection-error'
          ) {
            // We could auto retry here but for consistent session audio/video
            // it's probably safest to let the retry be via a user interaction
            showErrorToast(t('telehealth-core:waitingRoom.failedToConnectToTheMeetingPleaseTryAgain'));
            logger.error('Connection failure when joining room', {}, error);
            void trackJoinSessionError(error);
            return;
          }

          showErrorToast(t('telehealth-core:waitingRoom.thereWasAnIssueJoiningTheMeeting'));
          logger.error('Failed to join room', {}, error as Error);
          void trackJoinSessionError(error);
          clearAdmissionState();
        },
      });

      if (!room) {
        setVisitorState(VisitorState.WAITING);
        void updateVisitorPresence();
        return null;
      }

      setVisitorState(VisitorState.MEETING);
      logger.info('Client joined session');
      void updateVisitorPresence();
      setPresessionVibesOpen(false);
      if (conversationSid) {
        toast.closeByTag(NEW_MESSAGE_TOAST_TAG);
        setActiveConversationSid(conversationSid);
      }

      clearPreSessionPrompt();
      setDefaultMeetingRoomFaviconAndTitle(t, activeBrand);

      return room;
    }, 'useVisitorConnectRoomCallback');
  }, [
    setVisitorState,
    getSessionConversationSid,
    setJoinAttempts,
    gracefullyCoerceVideoPlatform,
    connectRoom,
    getVisitorName,
    setPresessionVibesOpen,
    clearPreSessionPrompt,
    t,
    clearAdmissionState,
    getAdmissionFlow,
    setActiveConversationSid,
    activeBrand,
    updateVisitorPresence,
    trackJoinSessionError,
  ]);

  return visitorConnectRoom;
}
